import { convertLegacyStrokes } from './markupGeometry';

/**
 * Builds the annotated PDF of a document markup (BBD-126).
 *
 * The original file is kept as it is and every stroke is added on top of its
 * page as a vector path, so the PDF's text stays selectable and the file
 * stays small. Image markups (PNG/JPG) become a one-page PDF the size of the
 * image. pdf-lib is loaded on demand: most sessions never export.
 */

export class MarkupExportError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'MarkupExportError';
        this.code = code;
    }
}

const OPERATOR_CHUNK = 5000;

const isPdfBytes = (bytes) => {
    // "%PDF" may be preceded by a few junk bytes
    const limit = Math.min(bytes.length - 4, 1024);
    for (let i = 0; i <= limit; i++) {
        if (bytes[i] === 0x25 && bytes[i + 1] === 0x50 && bytes[i + 2] === 0x44 && bytes[i + 3] === 0x46) return true;
    }
    return false;
};

const isPngBytes = (bytes) => bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
const isJpegBytes = (bytes) => bytes[0] === 0xff && bytes[1] === 0xd8;

/**
 * EXIF orientation of a JPEG (1 = upright; 1 as well when there is none).
 * Browsers apply it when they show the image, pdf-lib does not.
 */
export const readJpegOrientation = (bytes) => {
    try {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        let offset = 2;
        while (offset + 4 <= view.byteLength) {
            const marker = view.getUint16(offset);
            if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) break;
            const size = view.getUint16(offset + 2);
            if (marker === 0xffe1 && view.getUint32(offset + 4) === 0x45786966) {
                const tiff = offset + 10;
                const little = view.getUint16(tiff) === 0x4949;
                const ifd = tiff + view.getUint32(tiff + 4, little);
                const count = view.getUint16(ifd, little);
                for (let i = 0; i < count; i++) {
                    const entry = ifd + 2 + i * 12;
                    if (view.getUint16(entry, little) === 0x0112) {
                        return view.getUint16(entry + 8, little);
                    }
                }
                return 1;
            }
            offset += 2 + size;
        }
    } catch (err) {
        // Truncated or unusual header: treat the image as upright.
    }
    return 1;
};

// Redraw a rotated photo the way the browser shows it, as an upright JPEG.
const reencodeUpright = async (bytes) => {
    const url = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
    try {
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('image'));
            img.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        canvas.getContext('2d').drawImage(image, 0, 0);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        if (!blob) throw new Error('image');
        return new Uint8Array(await blob.arrayBuffer());
    } finally {
        URL.revokeObjectURL(url);
    }
};

const normalizeRotation = (angle) => {
    if (!Number.isFinite(angle) || angle % 90 !== 0) return 0;
    return ((angle % 360) + 360) % 360;
};

const normalizeBox = ({ x, y, width, height }) => ({
    x1: Math.min(x, x + width),
    y1: Math.min(y, y + height),
    x2: Math.max(x, x + width),
    y2: Math.max(y, y + height),
});

/**
 * How a PDF page maps to the page space the strokes are stored in.
 *
 * Page space is what pdf.js shows at scale 1: the visible box of the page
 * (CropBox within MediaBox), turned by the page's /Rotate, with the origin at
 * the top-left corner. `matrix` takes a page-space point to PDF user space,
 * whose origin is at the bottom-left with the y axis pointing up.
 */
export const pageGeometry = (page) => {
    const media = normalizeBox(page.getMediaBox());
    const crop = normalizeBox(page.getCropBox());
    let box = {
        x1: Math.max(media.x1, crop.x1),
        y1: Math.max(media.y1, crop.y1),
        x2: Math.min(media.x2, crop.x2),
        y2: Math.min(media.y2, crop.y2),
    };
    if (box.x2 - box.x1 <= 0 || box.y2 - box.y1 <= 0) box = media;

    const { x1, y1, x2, y2 } = box;
    const width = x2 - x1;
    const height = y2 - y1;

    switch (normalizeRotation(page.getRotation().angle)) {
        case 90:
            return { size: { width: height, height: width }, matrix: [0, 1, 1, 0, x1, y1] };
        case 180:
            return { size: { width, height }, matrix: [-1, 0, 0, 1, x2, y1] };
        case 270:
            return { size: { width: height, height: width }, matrix: [0, -1, -1, 0, x2, y2] };
        default:
            return { size: { width, height }, matrix: [1, 0, 0, -1, x1, y2] };
    }
};

const parseColor = (color) => {
    const hex = String(color || '').trim().replace(/^#/, '');
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (!/^[0-9a-f]{6}$/i.test(full)) return [0, 0, 0];
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
};

// The editor skips strokes with fewer than two points; so does the export.
const isDrawable = (stroke) => Array.isArray(stroke?.points) && stroke.points.length >= 2;

const drawStrokes = (pdfLib, doc, page, matrix, strokes) => {
    const {
        PDFName, pushGraphicsState, popGraphicsState, concatTransformationMatrix,
        setGraphicsState, setStrokingColor, setLineWidth, setLineCap, setLineJoin,
        moveTo, lineTo, stroke: strokePath, rgb, LineCapStyle, LineJoinStyle,
    } = pdfLib;

    // One transparency state per opacity, so highlighter lets the text show through
    const opacityStates = new Map();
    const opacityState = (opacity) => {
        if (!opacityStates.has(opacity)) {
            const name = `BBMarkupGS${opacityStates.size}`;
            page.node.setExtGState(
                PDFName.of(name),
                doc.context.obj({ Type: 'ExtGState', CA: opacity, ca: opacity })
            );
            opacityStates.set(opacity, name);
        }
        return opacityStates.get(opacity);
    };

    const push = (operators) => {
        for (let i = 0; i < operators.length; i += OPERATOR_CHUNK) {
            page.pushOperators(...operators.slice(i, i + OPERATOR_CHUNK));
        }
    };

    push([pushGraphicsState(), concatTransformationMatrix(...matrix)]);

    strokes.forEach((stroke) => {
        const [r, g, b] = parseColor(stroke.style?.color);
        const opacity = Math.min(Math.max(Number(stroke.style?.opacity) || 1, 0), 1);
        const operators = [pushGraphicsState()];

        if (opacity < 1) operators.push(setGraphicsState(opacityState(opacity)));
        operators.push(
            setStrokingColor(rgb(r, g, b)),
            setLineWidth(Number(stroke.style?.thickness) || 2),
            setLineCap(LineCapStyle.Round),
            setLineJoin(LineJoinStyle.Round),
            moveTo(stroke.points[0].x, stroke.points[0].y)
        );
        for (let i = 1; i < stroke.points.length; i++) {
            operators.push(lineTo(stroke.points[i].x, stroke.points[i].y));
        }
        operators.push(strokePath(), popGraphicsState());
        push(operators);
    });

    push([popGraphicsState()]);
};

const UNREADABLE_PDF = 'This PDF could not be processed for export. You can use Print instead.';

const loadPdf = async (pdfLib, fileBytes) => {
    let doc;
    try {
        // Encryption is checked below: pdf-lib's own error for it cannot be
        // told apart from other load errors once the library is transpiled.
        doc = await pdfLib.PDFDocument.load(fileBytes, { ignoreEncryption: true });
    } catch (err) {
        throw new MarkupExportError('unreadable', UNREADABLE_PDF);
    }
    if (doc.isEncrypted) {
        throw new MarkupExportError(
            'encrypted',
            'This PDF is encrypted or password-protected, so an annotated copy cannot be created. ' +
            'Your annotations are still saved here, and you can use Print instead.'
        );
    }
    return doc;
};

const imageToPdf = async (pdfLib, fileBytes) => {
    const doc = await pdfLib.PDFDocument.create();
    let image;
    try {
        if (isPngBytes(fileBytes)) {
            image = await doc.embedPng(fileBytes);
        } else if (isJpegBytes(fileBytes)) {
            const upright = readJpegOrientation(fileBytes) > 1 ? await reencodeUpright(fileBytes) : fileBytes;
            image = await doc.embedJpg(upright);
        }
    } catch (err) {
        image = null;
    }
    if (!image) {
        throw new MarkupExportError('unreadable', 'This image could not be processed for export.');
    }

    // One PDF unit per image pixel: the page is the image, the same size.
    const page = doc.addPage([image.width, image.height]);
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
    return doc;
};

/**
 * Build the annotated PDF.
 *
 * strokesByPage: { [pageNumber]: stroke[] }, page numbers starting at 1.
 * onlyAnnotatedPages: leave out the pages that have no annotations.
 * legacyWorkspace: the workspace size assumed for strokes saved before
 *   BBD-125 (see markupGeometry.js).
 *
 * Resolves to { bytes, pageCount, strokeCount, legacyConverted }.
 */
export const buildAnnotatedPdf = async ({ fileBytes, strokesByPage = {}, onlyAnnotatedPages = false, legacyWorkspace }) => {
    const pdfLib = await import('pdf-lib');

    let doc = isPdfBytes(fileBytes)
        ? await loadPdf(pdfLib, fileBytes)
        : await imageToPdf(pdfLib, fileBytes);

    const annotatedIndexes = [];
    let strokeCount = 0;
    let legacyConverted = 0;

    try {
        doc.getPages().forEach((page, index) => {
            const pageStrokes = strokesByPage[index + 1] || [];
            if (pageStrokes.length === 0) return;

            const { size, matrix } = pageGeometry(page);
            const { strokes, converted } = convertLegacyStrokes(pageStrokes, size, legacyWorkspace);
            const drawable = strokes.filter(isDrawable);
            if (drawable.length === 0) return;

            drawStrokes(pdfLib, doc, page, matrix, drawable);
            annotatedIndexes.push(index);
            strokeCount += drawable.length;
            legacyConverted += converted;
        });
    } catch (err) {
        // A damaged file can load and still have no usable pages
        throw new MarkupExportError('unreadable', UNREADABLE_PDF);
    }

    if (onlyAnnotatedPages) {
        if (annotatedIndexes.length === 0) {
            throw new MarkupExportError('no-annotations', 'This document has no annotations yet. Choose "All pages" to export it anyway.');
        }
        if (annotatedIndexes.length < doc.getPageCount()) {
            // Copying into a fresh document leaves the other pages out of the file entirely
            const subset = await pdfLib.PDFDocument.create();
            const pages = await subset.copyPages(doc, annotatedIndexes);
            pages.forEach((page) => subset.addPage(page));
            doc = subset;
        }
    }

    const bytes = await doc.save();
    return { bytes, pageCount: doc.getPageCount(), strokeCount, legacyConverted };
};
